import { lazy, Suspense, useCallback, useEffect, useState } from "react"
import { Analytics } from "@vercel/analytics/react"
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom"
import { Navbar } from "@/components/navbar"
import { AgeGate } from "@/components/age-gate"
import { HomePage } from "@/pages/home"
import { Footer } from "@/components/footer"
import { BackgroundPlants } from "@/components/background-plants"
import {
  CookieConsentBanner,
  type CookieConsentChoice,
  readCookieConsent,
} from "@/components/cookie-consent"
import { GoogleAnalyticsPageViews } from "@/components/google-analytics-page-views"
import { GoogleReviewSchema } from "@/components/google-review-schema"

const SmakiPage = lazy(() =>
  import("@/pages/smaki").then((module) => ({ default: module.SmakiPage }))
)
const OgloszeniaPage = lazy(() =>
  import("@/pages/ogloszenia").then((module) => ({
    default: module.OgloszeniaPage,
  }))
)
const AsortymentPage = lazy(() =>
  import("@/pages/asortyment").then((module) => ({
    default: module.AsortymentPage,
  }))
)
const GaleriaPage = lazy(() =>
  import("@/pages/galeria").then((module) => ({ default: module.GaleriaPage }))
)

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center pt-24">
      <div className="size-8 animate-spin rounded-full border-2 border-zielarz-green border-t-transparent" />
    </div>
  )
}

export function App() {
  const [cookieConsent, setCookieConsent] =
    useState<CookieConsentChoice | null>(() => readCookieConsent())
  const handleCookieConsentChange = useCallback(
    (choice: CookieConsentChoice) => {
      setCookieConsent(choice)
    },
    []
  )

  return (
    <BrowserRouter>
      <ScrollToTop />
      <GoogleAnalyticsPageViews consent={cookieConsent} />
      <GoogleReviewSchema />
      <div className="relative min-h-screen overflow-hidden">
        <BackgroundPlants />
        <div className="relative z-10 min-h-screen">
          <AgeGate />
          <Navbar />
          <main>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/ogloszenia" element={<OgloszeniaPage />} />
                <Route path="/asortyment" element={<AsortymentPage />} />
                <Route
                  path="/new-stuff"
                  element={<Navigate to="/asortyment" replace />}
                />
                <Route path="/galeria" element={<GaleriaPage />} />
                <Route
                  path="/smaki"
                  element={
                    <>
                      <SmakiPage />
                      <Footer />
                    </>
                  }
                />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
      <CookieConsentBanner onChoiceChange={handleCookieConsentChange} />
      {cookieConsent === "accepted" ? <Analytics /> : null}
    </BrowserRouter>
  )
}

export default App
